import { create } from 'zustand';
import { Quest, Lesson, Element, Chapter, ElementMapValue, ElementType, ModeType } from '@/constant/schema';
import { useMessageStore } from '@/store/useMessageStore';
import useAuthStore from '@/store/useAuthStore';
import ElementStatusService from '@/lib/elementStatusService';
import UserQuestProgressService, { UserQuestProgress } from '@/lib/userQuestProgressService';

import yaml from 'yaml';
import { getQuestById } from '@/lib/questService';
import { AccessElementTypes } from '@/constant/element-type-group';
import { getQuestGrades } from '@/lib/schoolDistrictGradeUtils';

interface ChatStore {
  loading: boolean;
  questId: string;
  chapterId: string;
  quest: Quest | null;
  mode: ModeType;
  element_map: Record<string, ElementMapValue>;
  current_chapter: Chapter | null;
  current_lesson: Lesson | null;
  current_element: Element | null;
  can_continue: boolean;
  lesson_finished: boolean;
  reviewMode: boolean; // Review mode flag

  // Generic element display state storage (will not persist to database)
  element_display_states: Record<string, any>;

  initQuest: (questId: string, chapterId: string, lessonId: string, mode: ModeType) => Promise<void>;
  next_element: () => boolean | null;
  get_lesson_and_element_index: (lesson_id?: string, element_id?: string) => { lessonIndex: number, elementIndex: number };
  getContext: () => string;
  element_finished: (element_id: string, userData?: any) => void;
  is_element_finished: (element_id: string) => boolean;
  uninstall: () => void;

  // Generic methods
  setElementDisplayState: (elementId: string, displayData: any) => void;
  getElementDisplayState: (elementId: string) => any;
  clearElementDisplayState: (elementId: string) => void;

  // Review mode methods
  enterReviewMode: () => void;
  exitReviewMode: () => void;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  loading: false,
  questId: '',
  chapterId: '0',
  quest: null,
  mode: ModeType.LEARN,
  current_chapter: null,
  current_lesson: null,
  current_element: null,
  element_map: {},
  // Used to assist in controlling whether cards can click the continue button. For interactive cards, first fill in InteractiveElementTypes, then just set to true in specific cards to make continue button available
  can_continue: true,
  lesson_finished: false,
  reviewMode: false,
  element_display_states: {},

  initQuest: async (questId: string, chapterId: string, lessonId: string, mode: ModeType) => {
    const loading = get().loading;
    if (loading) {
      return;
    }
    set({ loading: true, mode: mode });
    try {
      // Get user to determine if we need user-specific data
      const user = useAuthStore.getState().user;
      
      // Phase 1: Execute all independent data requests in parallel
      const dataPromises: Promise<any>[] = [
        getQuestById(questId, chapterId, lessonId)
      ];
      
      // Only execute user-related requests in parallel when user is logged in
      if (user) {
        dataPromises.push(
          ElementStatusService.loadFinishedElementsForLesson(user.id, lessonId),
          UserQuestProgressService.getProgress({
            user_id: user.id,
            quest_id: questId,
            chapter_id: chapterId,
            lesson_id: lessonId,
            mode_type: mode
          })
        );
      }
      
      // Wait for all parallel requests to complete
      const results = await Promise.all(dataPromises);
      let quest = results[0];
      
      if (mode === ModeType.LEARN) {
        quest!.chapters.forEach((chapter: Chapter) => {
          chapter.lesson_list.forEach((lesson: Lesson) => {
            lesson.elements = lesson.elements.filter((element: Element) => 
              AccessElementTypes.includes(element.element_type as ElementType)
            );
          });
        });  
      }

      if (!quest) {
        throw new Error(`Quest with ID ${questId} not found`);
      }
      set({ questId: questId, chapterId: chapterId });

      const chapter = quest.chapters.find((chapter: Chapter) => chapter.id === chapterId);

      if (!chapter) {
        throw new Error(`Chapter with ID ${chapterId} not found`);
      }

      const element_map = quest!.chapters!.flatMap((chapter: Chapter) => chapter.lesson_list).reduce((acc: Record<string, ElementMapValue>, lesson: Lesson) => {
        lesson.elements.forEach((element: Element) => {
          acc[element.id] = {
            ...element,
            finished: false
          };
        });
        return acc;
      }, {} as Record<string, ElementMapValue>);
      let lesson_finished_l = false;

      // Process user data from parallel requests
      let userProgress: UserQuestProgress | null = null;
      console.log('🐘 lessonId', {
        lessonId,
        lesson_list: chapter!.lesson_list
      });
      let current_lesson = chapter!.lesson_list.find((lesson: Lesson) => lesson.id === lessonId);
      if (!current_lesson) {
        throw new Error(`Lesson with ID ${lessonId} not found`);
      }
      let current_element = current_lesson.elements[0];
      let current_chapterId = chapterId;

      if (user && results.length > 1) {
        try {
          // Extract data from parallel results
          const finishedElements = results[1];
          userProgress = results[2];

          // Update completion status of elements in element_map
          if (Object.keys(finishedElements).length > 0) {
            console.log(`Found ${Object.keys(finishedElements).length} finished elements in Supabase`);
            for (const elementId in finishedElements) {
              if (element_map[elementId]) {
                element_map[elementId].finished = finishedElements[elementId].finished;
                element_map[elementId].data = finishedElements[elementId].data;
              }
            }
          }

          // Ensure userProgress is not null before using its properties
          if (userProgress && userProgress.lesson_id && userProgress.element_id) {
            console.log(`Found saved progress in Supabase: chapter=${chapterId}, lesson=${userProgress.lesson_id}, element=${userProgress.element_id}`);
            
            // Find corresponding lesson and element
            const savedLesson = chapter!.lesson_list.find((lesson: Lesson) => lesson.id === userProgress?.lesson_id);
            if (savedLesson) {
              current_lesson = savedLesson;
              const savedElement = savedLesson.elements.find((element: Element) => element.id === userProgress?.element_id);
              if (savedElement) {
                current_element = savedElement;
              }
            }
            // Determine if current_element is the last element of current_lesson
            if (current_element?.id === current_lesson?.elements[current_lesson?.elements.length - 1]?.id) {
              lesson_finished_l = true;
            }
          } else {
            console.log("No saved progress found in Supabase");
            UserQuestProgressService.upsertProgress({
              user_id: user.id,
              quest_id: questId,
              chapter_id: chapterId,
              lesson_id: current_lesson.id,
              element_id: current_element.id,
              mode_type: mode,
              progress: 0
            });
          }
          
        } catch (error) {
          console.error('Error loading data from Supabase:', error);
        }
      }

      // Phase 2: Load messages (requires quest data, and should also run for guests
      // so the welcome message + first element can be initialized locally)
      await useMessageStore.getState().loadMessages(questId, current_chapterId, current_lesson.id, quest, mode);

      console.log("🚗🚗🚗 quest", quest);
      set({
        loading: false,
        quest, 
        current_chapter: chapter!,
        current_lesson,
        current_element,
        element_map,
        lesson_finished: lesson_finished_l,
      });
    } catch (error) {
      console.error('Error initializing quest:', error);
    }
  },

 // Complete an element and get score
  element_finished: (element_id: string, element_data?: any) => {
    // Check if element_id exists in element_map
    if (!get().element_map[element_id]) {
      console.warn(`Element ${element_id} not found in element_map, skipping update`);
      return;
    }

    // Update local state (always update regardless of review mode)
    get().element_map[element_id].finished = true;
    get().element_map[element_id].data = element_data || {};
    let newElementMap = {...get().element_map};
    set({ element_map: newElementMap });

    // Get review mode state
    const reviewMode = get().reviewMode;

    // Only sync to Supabase in non-review mode
    const user = useAuthStore.getState().user;
    if (user && !reviewMode) {
      // Sync to Supabase
      const elementStatus = {
        user_id: user.id,
        quest_id: get().questId,
        chapter_id: get().chapterId,
        lesson_id: get().current_lesson?.id || '',
        element_id: element_id,
        finished: true,
        data: element_data || {}
      };

      // Asynchronously update Supabase, don't wait for result
      ElementStatusService.updateElementStatus(elementStatus)
        .then(result => {
          if (result) {
            console.log('Element status updated in Supabase:', result);
          }
        })
        .catch(error => {
          console.error('Failed to update element status in Supabase:', error);
        });
    } else if (reviewMode) {
      console.log('🔄 Review mode: skipping element status sync to database');
    }
  },
  
  next_element(): boolean | null {
    // Get current Lesson and Element
    const { current_lesson, current_element } = get();
    const lesson_id = current_lesson?.id || '';
    const element_id = current_element?.id || '';

    const elements = current_lesson?.elements || [];

    // Find index of current Element
    const currentElementIndex = elements.findIndex((element: Element) => element.id === element_id);
    if (currentElementIndex === -1) return null; // Current element_id is invalid

    let lesson_finished = false;
    let nextElement: Element | null = null;
    console.log('🐘 next_element', currentElementIndex, elements.length);

    // Check if there's a next Element
    if (currentElementIndex < (elements.length || 0) - 1) {
      nextElement = elements[currentElementIndex + 1];
    } else { // Current lesson is completed
      nextElement = elements[currentElementIndex];
      lesson_finished = true;
    }

    set({ 
      current_element: nextElement,
    });
    return !lesson_finished;
  },
  
  getContext: () => {
    const current_chapter = get().current_chapter;
    const quest = get().quest;
    
    if (!current_chapter || !quest) return '';
    
    const questInfo = {
      id: quest.id,
      title: quest.title,
      description: quest.description,
      book_name: quest.book_name,
      subject: quest.subject,
      grade: getQuestGrades(quest),
      instructor_profile: quest.instructor_profile,
    };
    
    const contextObj = {
      quest: questInfo,
      current_chapter: {
        id: current_chapter.id,
        title: current_chapter.title,
        description: current_chapter.description,
        lessons: current_chapter.lesson_list.map((lesson) => {
          const elements = lesson.elements.map((element) => {
            return {
              id: element.id,
              title: element.title,
              type: 'element',
              element_type: element.element_type,
            };
          });
          
          return {
            id: lesson.id,
            title: lesson.title,
            type: 'lesson',
            description: lesson.description,
            elements,
          };
        })
      }
    };

    // Convert to yaml string
    const context = yaml.stringify(contextObj);
    return context;
  },

  is_learning_finished: () => {
    const current_lesson = get().current_lesson;
    const current_element = get().current_element;
    // If current_element is the last element of current_lesson, return true, otherwise false
    return current_element?.id === current_lesson?.elements[current_lesson?.elements.length - 1]?.id;
  },

  is_element_finished: (element_id: string) => {
    return get().element_map[element_id]?.finished || false;
  },

  get_lesson_and_element_index: (lesson_id?: string, element_id?: string) => {
    if (!lesson_id) {
      lesson_id = get().current_lesson?.id || '';
    }
    if (!element_id) {
      element_id = get().current_element?.id || '';
    }
    const lessonIndex = get().current_lesson?.order || 0;
    const elementIndex = get().current_lesson?.elements?.findIndex(
      (element: Element) => element.id === element_id) || 0;
    return { lessonIndex: lessonIndex, elementIndex: elementIndex };
  },

  uninstall: () => {
    try {
      set({ 
        quest: null, 
        current_chapter: null, 
        current_lesson: null, 
        current_element: null, 
        element_map: {},
        element_display_states: {} 
      });
    } catch (error) {
      console.error('Error in uninstall:', error);
      // Ensure state is reset even if error occurs
      set({ 
        quest: null, 
        current_chapter: null, 
        current_lesson: null, 
        current_element: null, 
        element_map: {},
        element_display_states: {} 
      });
    }
  },
  
  getElementDisplayState: (elementId: string) => {
    return get().element_display_states[elementId];
  },

  setElementDisplayState: (elementId: string, displayData: any) => {
    // 如果原来id的displayData存在，则覆盖，否则直接设置
    const existingState = get().element_display_states[elementId];
    if (existingState) {
      set(state => ({
        element_display_states: {
          ...state.element_display_states,
          [elementId]: { ...existingState, ...displayData }
        }
      }));
    } else {
      set(state => ({
        element_display_states: {
          ...state.element_display_states,
          [elementId]: displayData
        }
      }));
    }
  },

  clearElementDisplayState: (elementId: string) => {
    set(state => {
      const newStates = { ...state.element_display_states };
      delete newStates[elementId];
      return { element_display_states: newStates };
    });
  },

  // Enter review mode - reset lesson without affecting database
  enterReviewMode: () => {
    const { current_lesson, element_map, questId, chapterId, quest, mode } = get();

    if (!current_lesson || !quest) {
      console.error('Cannot enter review mode: missing lesson or quest data');
      return;
    }

    console.log('🔄 Entering review mode for lesson:', current_lesson.id);

    // 1. Set review mode flag
    set({ reviewMode: true });

    // 2. Create new element_map copy, reset all elements in current lesson
    const resetElementMap = { ...element_map };
    current_lesson.elements.forEach(element => {
      if (resetElementMap[element.id]) {
        resetElementMap[element.id] = {
          ...resetElementMap[element.id],
          finished: false,
          data: undefined, // Clear user answer data
        };
      }
    });

    // 3. Reset all related states
    set({
      element_map: resetElementMap,
      element_display_states: {}, // Clear all UI display states
      current_element: current_lesson.elements[0], // Reset to first element
      can_continue: true,
      lesson_finished: false,
    });

    // 4. Clear and reinitialize messages
    useMessageStore.getState().clearAndReinitialize(
      questId,
      chapterId,
      current_lesson.id,
      quest,
      mode
    );

    console.log('✅ Review mode activated');
  },

  // Exit review mode and restore real learning progress
  exitReviewMode: () => {
    const { questId, chapterId, current_lesson, mode } = get();

    console.log('🔄 Exiting review mode, restoring real progress...');

    // Reload real learning progress
    if (current_lesson) {
      get().initQuest(questId, chapterId, current_lesson.id, mode);
    }

    // Turn off review mode flag
    set({ reviewMode: false });

    console.log('✅ Review mode exited, real progress restored');
  }
}));
